/**
 * QSFloatingCTA — persistent bottom-right floating action button.
 *
 * Subtle but prominent. Hovers over the bottom-right corner and stays
 * pinned as the user scrolls the dashboard. Click to expand a small
 * popover containing the QSCallCTA card (full pricing/redeem logic).
 *
 * - Pro / Pro+ subscribers see a "free this month" badge with remaining count.
 * - Everyone else sees a plain phone icon → £49 paid checkout on click.
 * - Auto-hides on the SCOPE drawing canvas page where screen real-estate
 *   is at a premium (caller decides via mounting placement).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Phone, X } from 'lucide-react';
import { QSCallCTA } from './QSCallCTA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QSFloatingCTA = ({ userEmail = '', draftId = null }) => {
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/billing/me`)
      .then((r) => { if (!cancelled) setBilling(r.data); })
      .catch(() => { if (!cancelled) setBilling(null); });
    return () => { cancelled = true; };
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const callsRemaining = billing?.monthly_allowances?.quarterly_calls_remaining
    ?? billing?.monthly_allowances?.qs_calls_remaining ?? 0;
  const isSubscriber = billing?.tier === 'pro' || billing?.tier === 'pro_plus';
  const showFreeBadge = isSubscriber && callsRemaining > 0;

  return (
    <>
      {/* Floating circular trigger */}
      <button
        ref={buttonRef}
        data-testid="qs-floating-cta-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Talk to a chartered QS"
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0F2742 0%, #1E40AF 100%)',
          border: '2px solid rgba(255,255,255,0.2)',
          color: '#fff',
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(15, 39, 66, 0.32), 0 0 0 6px rgba(37, 99, 235, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
          transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.06)';
          e.currentTarget.style.boxShadow = '0 12px 30px rgba(15, 39, 66, 0.4), 0 0 0 10px rgba(37, 99, 235, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(15, 39, 66, 0.32), 0 0 0 6px rgba(37, 99, 235, 0.08)';
        }}
      >
        {open ? <X size={22} /> : <Phone size={22} />}
        {/* Subscriber free-call badge */}
        {!open && showFreeBadge && (
          <span
            data-testid="qs-floating-cta-badge"
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 22,
              height: 22,
              borderRadius: 999,
              background: '#10B981',
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
              border: '2px solid #fff',
              boxShadow: '0 2px 6px rgba(16,185,129,0.35)',
            }}
          >
            {callsRemaining}
          </span>
        )}
        {/* Pulsing halo to draw attention without being noisy */}
        {!open && (
          <span
            style={{
              position: 'absolute',
              inset: -2,
              borderRadius: '50%',
              border: '2px solid rgba(37, 99, 235, 0.5)',
              animation: 'qs-pulse 2.4s ease-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
      </button>

      {/* Popover card */}
      {open && (
        <div
          ref={popoverRef}
          data-testid="qs-floating-cta-popover"
          role="dialog"
          aria-label="Book a QS Review Call"
          style={{
            position: 'fixed',
            right: 24,
            bottom: 96,
            width: 360,
            maxWidth: 'calc(100vw - 48px)',
            zIndex: 9999,
            animation: 'qs-pop-in 0.18s ease-out',
          }}
        >
          <QSCallCTA
            variant="card"
            userEmail={userEmail}
            draftId={draftId}
            showRedeem
            testid="qs-floating-cta"
          />
        </div>
      )}

      {/* Inline keyframes — scoped to the button via animation name */}
      <style>{`
        @keyframes qs-pulse {
          0%   { transform: scale(1);    opacity: 0.65; }
          80%  { transform: scale(1.32); opacity: 0;    }
          100% { transform: scale(1.32); opacity: 0;    }
        }
        @keyframes qs-pop-in {
          0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0)   scale(1);    }
        }
      `}</style>
    </>
  );
};

export default QSFloatingCTA;
